import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  ListItemIcon,
  Switch,
  Typography,
} from '@mui/material';
import { formControlLabelClasses } from '@mui/material/FormControlLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import _ from 'lodash';
import React, { Fragment } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { fetchCloneOptions, queueClone } from '@sophocles/api/odlApp';
import TransferList from '@sophocles/components/TransferList';
import { fetchSitesForSiteGroup } from '@sophocles/entities/Site';
import { smartlinkUnitSelector } from '@sophocles/entities/SmartlinkUnit';
import {
  getAuthUserCustomerPreferencesSelector,
  getSelectedCustomerSelector,
} from '@sophocles/features/auth/authSelectors';
import { selectSiteUnit } from '@sophocles/features/entities/site';
import { getStructuresForPlant, selectedPlant } from '@sophocles/features/sites/slices/selectedPlant/selectors';
import { addToast } from '@sophocles/features/toast';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import useSiteTerm from '@sophocles/hooks/useSiteTerm';
import {
  buildCloneOptionsPayload,
  parseCloneOptionsResponse,
} from '@sophocles/views/sites/components/cloneOptionsForm';

const formControlStyles = {
  [formControlLabelClasses.root]: {
    textAlign: 'center',
    fontSize: '0.8rem',
    '& label': { fontSize: '0.8rem', marginRight: 0, marginLeft: 0 },
  },
};

class CloneUnitModal extends React.Component {
  constructor(props) {
    super(props);
    const { initialSite } = props;
    this.state = {
      open: false,
      canSubmit: false,
      masterSite: initialSite || '',
      sites: {},
      cloneOptions: {},
      loadingOptions: false,
      showAdvanced: false,
      visible: true,
    };
  }

  // Fetches are deferred to openDialog so the modal makes no network requests
  // until the user opens it.

  fetchData = () => {
    const { initialSite, fetchStructuresForPlant, selectedPlant: plant } = this.props;

    const optionsFetch =
      initialSite && this.hasUnitModel(initialSite) ? fetchCloneOptions(initialSite) : Promise.resolve(null);
    const structuresFetch = fetchStructuresForPlant(plant);

    return Promise.all([optionsFetch, structuresFetch]);
  };

  openDialog = () => {
    const { initialSite, t, siteTerm, siteGroupTerm } = this.props;

    // Re-entry guard: extra clicks while the dialog is open or its data is
    // still loading must not start another fetch round.
    if (this.state.open || this.state.loading) return;

    // Open immediately with a progress state; the fetch fills the dialog in.
    // fetchData already gets fresh clone options for this open, so one round
    // trip is the whole wait.
    this.setState({ open: true, loading: true, loadingOptions: true });
    this.fetchData()
      .then(([cloneResult, structures]) => {
        if (cloneResult) {
          // Populate destinations from the clone options response so we don't
          // depend on the legacy structures fetch for the initialSite flow.
          const sitesFromDestinations = (cloneResult.available_destinations || []).reduce((acc, dest) => {
            acc[dest.id] = dest.name;
            return acc;
          }, {});
          this.setState({
            cloneOptions: this._parseCloneOptions(cloneResult.options),
            ...(initialSite ? { sites: sitesFromDestinations } : {}),
          });
        }

        if (!initialSite) {
          // No-initialSite mode: populate sites from structures for master selection.
          if (structures && !structures.error) {
            this.setState({
              sites: structures.sites.toList().reduce((r, c) => {
                r[c.id] = c.name;
                return r;
              }, {}),
            });
          } else if (structures && structures.error) {
            alert(
              t('sites.cloneFetchStructuresError', 'Unable to fetch {{sites}} for {{siteGroup}}.', {
                sites: siteTerm.plural.toLowerCase(),
                siteGroup: siteGroupTerm.singular.toLowerCase(),
              }),
            );
          }
        }
        this.setState({ loading: false, loadingOptions: false });
      })
      .catch(() => {
        // The options fetch failing means this unit cannot be cloned right
        // now — close and withdraw the affordance rather than leaving a
        // dialog that can never load.
        this.setState({ visible: false, open: false, canSubmit: false, loading: false, loadingOptions: false });
      });
  };

  closeDialog = () => {
    const { initialSite } = this.props;

    this.setState({
      open: false,
      canSubmit: false,
      loadingOptions: false,
      showAdvanced: false,
    });

    if (initialSite) {
      // Disable any selected options.
      this.toggleAllOptions(false);
    } else {
      // Clear out master site and clone options.
      this.setState({
        masterSite: '',
        cloneOptions: {},
      });
    }
  };

  handleChangeMasterSite = value => {
    if (!this.hasUnitModel(value)) {
      this.setState({ masterSite: value, loadingOptions: false, cloneOptions: {} });
      return;
    }

    this.setState({ loadingOptions: true });

    fetchCloneOptions(value)
      .then(res => {
        this.setState({
          loadingOptions: false,
          cloneOptions: this._parseCloneOptions(res.options),
        });
      })
      .catch(() => {
        this.setState({
          visible: false,
          open: false,
          canSubmit: false,
          loadingOptions: false,
        });
      });

    this.setState({ masterSite: value });
  };

  getIsRelayError = cloneOptions => {
    const isOnlyCloningSchedules =
      Object.entries(cloneOptions).filter(([key, co]) => co.enabled == true && key === 'schedules').length === 1;
    const hasToggledRelays =
      'schedules' in cloneOptions ? cloneOptions.schedules.relays.filter(r => r.enabled == true).length >= 1 : false;
    return isOnlyCloningSchedules && !hasToggledRelays;
  };

  getCanSubmit = (targets, cloneOptions) => {
    const hasTargets = targets && targets.length > 0;
    const enabledEntries = Object.entries(cloneOptions).filter(([, co]) => co.enabled == true);
    const isOnlyCloningSchedules = enabledEntries.length === 1 && enabledEntries[0][0] === 'schedules';
    const hasToggledRelays =
      'schedules' in cloneOptions ? cloneOptions.schedules.relays.filter(r => r.enabled == true).length >= 1 : false;
    if (isOnlyCloningSchedules) {
      return hasTargets && hasToggledRelays;
    }
    return hasTargets;
  };

  handleChangeTargets = targets => {
    this.setState({
      targets: targets,
      canSubmit: this.getCanSubmit(targets, this.state.cloneOptions),
    });
  };

  handleClickShowAdvanced = () => {
    this.setState({
      showAdvanced: !this.state.showAdvanced,
    });
  };

  setOption = (key, value) => {
    this.setState({
      cloneOptions: Object.assign({}, this.state.cloneOptions, {
        [key]: Object.assign({}, this.state.cloneOptions[key], { enabled: value }),
      }),
    });
  };

  toggleOption = key => {
    this.setOption(key, !this.state.cloneOptions[key].enabled);
  };

  toggleAllOptions = newValue => {
    const { cloneOptions } = this.state;

    const options = { ...cloneOptions };
    const keys = Object.keys(options);

    const toggleOff = keys.filter(key => options[key].enabled).length === keys.length;

    keys.forEach(key => {
      options[key].enabled = newValue != null ? newValue : !toggleOff;
    });

    this.setState({
      cloneOptions: options,
    });
  };

  handleToggleField = (key, fieldIndex) => {
    const cloneOptions = Object.assign({}, this.state.cloneOptions);

    const fieldValue = cloneOptions[key].fields[fieldIndex].enabled;
    cloneOptions[key].fields[fieldIndex].enabled = !fieldValue;

    this.setState({
      cloneOptions,
    });
  };

  handleToggleRelays = (key, newRelays) => {
    const cloneOptions = Object.assign({}, this.state.cloneOptions);

    cloneOptions[key].relays.forEach((r, i) => {
      cloneOptions[key].relays[i].enabled = false;
    });

    cloneOptions[key].relays.forEach((r, i) => {
      newRelays.forEach(nr => {
        if (nr === r.relay) {
          cloneOptions[key].relays[i].enabled = true;
        }
      });
    });

    this.setState({
      cloneOptions,
      canSubmit: this.getCanSubmit(this.state.targets, cloneOptions),
    });
  };

  handleToggleOption = (key, optionIndex) => {
    const cloneOptions = Object.assign({}, this.state.cloneOptions);

    const optionValue = cloneOptions[key].options[optionIndex].enabled;
    cloneOptions[key].options[optionIndex].enabled = !optionValue;

    this.setState({
      cloneOptions,
    });
  };

  confirm = () => {
    const { masterSite, targets, cloneOptions } = this.state;
    const { addToast: dispatchAddToast, t } = this.props;

    this.setState({ canSubmit: false });

    const optionsPayload = buildCloneOptionsPayload(cloneOptions);
    const destinations = (targets || []).map(id => ({ type: 'site', id: Number(id) }));

    queueClone(masterSite, { options: optionsPayload, destinations })
      .then(() => {
        dispatchAddToast({
          title: t('general.success', 'Success'),
          message: t('sites.unitSettingsCloned', 'Unit settings cloned.'),
          level: 'success',
          dismissible: true,
        });
      })
      .catch(() => {
        dispatchAddToast({
          title: t('general.error', 'Error'),
          message: t('sites.queueCloneFailed', 'Failed to queue clone.'),
          level: 'error',
          dismissible: true,
        });
      });

    this.closeDialog();
  };

  get unitModelMap() {
    const { smartLinkUnits } = this.props;
    const units = smartLinkUnits.toJS();
    const unitModelMap = {};
    Object.keys(units).forEach(key => {
      unitModelMap[units[key].site] = units[key].model;
    });
    return unitModelMap;
  }

  hasUnitModel = siteId => {
    const { initialSite, siteUnit } = this.props;
    // TODO(odl-app-contract): firmware_version not yet on the snapshot
    if (siteId === initialSite || siteId === Number(initialSite)) {
      return siteUnit?.model != null;
    }
    return this.unitModelMap[siteId] != null;
  };

  _parseCloneOptions(options) {
    return parseCloneOptionsResponse(options);
  }

  render() {
    const { loading, open, sites, masterSite, cloneOptions, canSubmit, showAdvanced, loadingOptions } = this.state;
    const { triggerItem, t, initialSite } = this.props;

    const enabledOptions = Object.keys(cloneOptions).filter(key => cloneOptions[key].enabled);
    const enabledOptionsWithOptions = enabledOptions.filter(key => cloneOptions[key].options.length > 0);
    const enabledOptionsWithFields = enabledOptions.filter(key => cloneOptions[key].fields.length > 0);

    const actions = [
      <Button key="cancel" variant="outlined" onClick={this.closeDialog}>
        {t('actions.cancel')}
      </Button>,
      <Button
        key="confirm"
        variant="contained"
        disabled={!canSubmit || enabledOptions.length === 0}
        onClick={this.confirm}
      >
        {t('actions.submit')}
      </Button>,
    ];

    return this.state.visible ? (
      <div style={{ display: 'inline-block' }}>
        {triggerItem ? (
          <div onClick={this.openDialog}>{triggerItem}</div>
        ) : (
          <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={this.openDialog}>
            {t('sites.clone')}
          </Button>
        )}

        <Dialog open={open} onClose={this.closeDialog}>
          <DialogTitle>{t('sites.cloneSettings')}</DialogTitle>
          <DialogContent dividers>
            {loading ? null : (
              <TransferList
                initial={masterSite}
                hideSelect={!!initialSite}
                data={sites}
                filterMap={this.unitModelMap}
                noMatchText={t('sites.cloneNoOptionsMessage')}
                getTooltip={siteId => {
                  if (this.props.structures) {
                    const structures = this.props.structures.toJS();
                    const hasPairedRelays = _.get(structures, [siteId, 'has_paired_relays'], false);

                    if (hasPairedRelays) {
                      return t('sites.pairedRelays', 'Paired Relays');
                    }
                  }

                  return null;
                }}
                renderRightIcon={siteId => {
                  if (this.props.structures) {
                    const structures = this.props.structures.toJS();
                    const hasPairedRelays = _.get(structures, [siteId, 'has_paired_relays'], false);

                    if (hasPairedRelays) {
                      return (
                        <ListItemIcon sx={{ minWidth: 0 }}>
                          <CloseFullscreenIcon />
                        </ListItemIcon>
                      );
                    }
                  }

                  return null;
                }}
                onChangeMaster={this.handleChangeMasterSite}
                onChangeTargets={this.handleChangeTargets}
              />
            )}
            <Typography sx={{ ...formControlStyles, textAlign: 'center', my: 4 }}>
              {t('sites.cloneDisclaimer')}
            </Typography>

            {this.state.loadingOptions ? (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <LinearProgress style={{ width: '50%' }} variant="indeterminate" />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {masterSite && (
                  <FormControlLabel
                    labelPlacement="bottom"
                    style={{ marginBottom: 18 }}
                    control={
                      <Switch
                        checked={enabledOptions.length === Object.keys(cloneOptions).length}
                        onChange={() => this.toggleAllOptions(null)}
                      />
                    }
                    label={
                      <div style={{ textAlign: 'center', minWidth: 180 }}>
                        <Typography sx={formControlStyles}>{t('actions.toggleAll')}</Typography>
                      </div>
                    }
                  />
                )}
                <span
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto auto auto',
                    textAlign: 'center',
                  }}
                >
                  {Object.keys(cloneOptions).map(key => (
                    <FormControlLabel
                      key={key}
                      labelPlacement="bottom"
                      style={{ marginBottom: 18, marginLeft: 0, marginRight: 0 }}
                      control={<Switch checked={cloneOptions[key].enabled} onChange={() => this.toggleOption(key)} />}
                      label={
                        <div style={{ textAlign: 'center', minWidth: 180 }}>
                          <Typography sx={formControlStyles}>{cloneOptions[key].label}</Typography>
                        </div>
                      }
                    />
                  ))}
                </span>
              </div>
            )}

            {this.getIsRelayError(this.state.cloneOptions) && (
              <div style={{ display: 'flex', textAlign: 'center', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="caption" color="error">
                  {t('sites.cloneNoRelaysMessage')}
                </Typography>
              </div>
            )}

            {enabledOptions.length > 0 && !loadingOptions ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                <button className="btn btn-secondary" onClick={this.handleClickShowAdvanced}>
                  <Typography sx={formControlStyles}>
                    {showAdvanced ? t('actions.hideAdvanced') : t('actions.showAdvanced')}
                  </Typography>
                </button>
              </div>
            ) : null}

            {showAdvanced && !loadingOptions ? (
              <div style={{ paddingTop: 16 }}>
                <Divider />
                <div style={{ fontWeight: 'bold', paddingTop: 16, paddingLeft: 32, paddingRight: 32 }}>
                  {enabledOptionsWithOptions.length > 0 ? <h5>{t('sites.cloneOptions')}</h5> : null}
                  {enabledOptionsWithOptions.map(key => {
                    return (
                      <div key={key} style={{ marginBottom: 16 }}>
                        <h6>{cloneOptions[key].label}</h6>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'auto auto auto',
                            paddingRight: 16,
                            paddingLeft: 16,
                          }}
                        >
                          {cloneOptions[key].options.map((opt, i) => (
                            <FormControlLabel
                              key={key + '-' + opt.field}
                              control={
                                <Checkbox
                                  style={{ width: 'auto' }}
                                  checked={cloneOptions[key].options[i].enabled}
                                  onClick={() => this.handleToggleOption(key, i)}
                                />
                              }
                              label={<div>{opt.label}</div>}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontWeight: 'bold', paddingTop: 16, paddingLeft: 32, paddingRight: 32 }}>
                  {enabledOptionsWithFields.length > 0 ? <h5>{t('sites.cloneFieldOptions')}</h5> : null}
                  {enabledOptionsWithFields.map(key => {
                    return (
                      <div key={key} style={{ marginBottom: 16 }}>
                        <h6>{cloneOptions[key].label}</h6>
                        {cloneOptions[key].relays && (
                          <Fragment>
                            <Typography variant="6">
                              {t('sites.relays')} (
                              {cloneOptions[key].relays
                                .filter(r => r.enabled)
                                .map(r => r.relay)
                                .join(', ')}
                              )
                            </Typography>
                            <ToggleButtonGroup
                              fullWidth
                              color="success"
                              value={cloneOptions[key].relays.filter(r => r.enabled).map(r => r.relay)}
                              onChange={(e, newRelays) => {
                                this.handleToggleRelays(key, newRelays);
                              }}
                              sx={{ mb: 2 }}
                            >
                              {cloneOptions[key].relays.map(r => (
                                <ToggleButton
                                  fullWidth
                                  key={`relay-${key}-${r.relay}`}
                                  value={r.relay}
                                  sx={{ width: '100%' }}
                                >
                                  <span style={{ fontWeight: r.enabled ? 'bold' : 'normal' }}>{r.relay}</span>
                                </ToggleButton>
                              ))}
                            </ToggleButtonGroup>
                          </Fragment>
                        )}
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '33% 33% 33%',
                            paddingRight: 16,
                            paddingLeft: 16,
                          }}
                        >
                          {cloneOptions[key].fields.map((field, i) => (
                            <FormControlLabel
                              key={key + '-' + field.field}
                              style={{ marginRight: 32 }}
                              control={
                                <Checkbox
                                  style={{ width: 'auto', marginRight: 0 }}
                                  checked={cloneOptions[key].fields[i].enabled}
                                  onClick={() => this.handleToggleField(key, i)}
                                />
                              }
                              label={<div>{field.label}</div>}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </DialogContent>
          <DialogActions>{actions}</DialogActions>
        </Dialog>
      </div>
    ) : null;
  }
}

function fetchPlantStructures(siteGroup) {
  return dispatch => {
    return dispatch(fetchSitesForSiteGroup(siteGroup, { has_no_paired_relays: true }));
  };
}

function mapStateToProps(state, ownProps) {
  return {
    customerPreferences: getAuthUserCustomerPreferencesSelector(state),
    customer: getSelectedCustomerSelector(state),
    selectedPlant: selectedPlant(state),
    structures: getStructuresForPlant(state),
    smartLinkUnits: smartlinkUnitSelector(state),
    siteUnit: selectSiteUnit(ownProps.initialSite)(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    fetchStructuresForPlant: siteGroupId => dispatch(fetchPlantStructures(siteGroupId)),
    addToast: toast => dispatch(addToast(toast)),
  };
}

const CloneUnitModalContainer = connect(mapStateToProps, mapDispatchToProps)(CloneUnitModal);

const TranslatedCloneUnitModal = withTranslation()(CloneUnitModalContainer);

function CloneUnitModalWithTerms(props) {
  const siteTerm = useSiteTerm();
  const siteGroupTerm = useSiteGroupTerm();
  return <TranslatedCloneUnitModal {...props} siteTerm={siteTerm} siteGroupTerm={siteGroupTerm} />;
}

export default CloneUnitModalWithTerms;
